/**
 * EmailThreadSubHeader — sub-header content for an ahoy_email_thread
 * slideout.
 *
 * One meta row under the title: the visibility badge (click → AccessPanel
 * popover) beside the compact participant avatar stack (click → full pill
 * list popover). Folding the thread's participant meta in here — instead of
 * a standalone "Participants" section — is what lets the first message start
 * high in the panel. The message count lives in the conversation's own meta
 * row, not here.
 *
 * Registered on `ENTITY_TYPE_REGISTRY.ahoy_email_thread.components.subHeader`;
 * the type's `summary` slot is explicitly `null`.
 */

import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import {
  EmailThreadVisibilityBadge,
  VISIBILITY_BADGE_MARGIN_COMPENSATION,
} from '../../components/EmailThreadVisibilityBadge';
import { EmailThreadParticipantsStack } from './email-thread-participants-stack';

export function EmailThreadSubHeader({ object }: { object: CrmObject }) {
  // Cancel the visibility badge's own mx (kept there for its inline call
  // sites — see the constant's doc) so the row's left edge aligns with the
  // title above. Applied only WHEN the badge renders — with no
  // access_summary the badge is null, and the compensation would shift the
  // participant stack past the title edge instead.
  const hasVisibilityBadge = !!object.access_summary;
  return (
    <div
      className={cx(
        'flex flex-wrap items-center gap-x-2 gap-y-1',
        hasVisibilityBadge && VISIBILITY_BADGE_MARGIN_COMPENSATION
      )}
    >
      <EmailThreadVisibilityBadge access_summary={object.access_summary} />
      <EmailThreadParticipantsStack object={object} />
    </div>
  );
}
